import type {
    CostGroupId,
    NodeItemId,
    PackageCostGroup,
    PackageNode,
    PackageNodeItem,
    PackageNodeItemOption,
    PackageQuestion,
    PackageQuestionOption,
    QuestionId,
    QuestionOptionId,
    TemplateNodeId,
    TemplatePackage,
} from './types';

/**
 * De graaf: het pakket één keer geïndexeerd zodat elke herberekening alleen nog
 * hoeft te wandelen. Bevat geen antwoorden en is dus herbruikbaar zolang de
 * templateversie dezelfde is.
 */

/** Vraagtypes die hun antwoord in `option_ids` dragen en meerdere selecties toestaan. */
const MULTI_TYPES = new Set(['options_multi', 'options_single_multi']);

/**
 * Waardetypes hebben geen betekenisvolle optie-id: is de vraag beantwoord, dan
 * worden onvoorwaardelijk alle randen van het item gevolgd.
 */
const VALUE_TYPES = new Set(['number', 'text', 'text_items']);

export interface GraphQuestion {
    readonly id: QuestionId;
    readonly question: PackageQuestion;
    readonly options: readonly PackageQuestionOption[];
    readonly optionsById: ReadonlyMap<QuestionOptionId, PackageQuestionOption>;
    readonly isMulti: boolean;
    readonly isValueType: boolean;
}

export interface GraphItem {
    readonly id: NodeItemId;
    readonly item: PackageNodeItem;
    readonly questionId: QuestionId;
    readonly isStart: boolean;
    /** Randen in optievolgorde — dát is de volgorde waarin takken worden gevolgd. */
    readonly options: readonly PackageNodeItemOption[];
}

export interface GraphNode {
    readonly id: TemplateNodeId;
    readonly node: PackageNode;
    readonly parent: GraphNode | null;
    readonly children: readonly GraphNode[];
    readonly items: readonly GraphItem[];
    readonly itemsById: ReadonlyMap<NodeItemId, GraphItem>;
    readonly startItem: GraphItem | null;
    readonly isMulti: boolean;
}

export type GraphIssueCode =
    | 'missing_parent'
    | 'missing_start_item'
    | 'duplicate_item_id'
    | 'duplicate_node_id'
    | 'unknown_question'
    | 'unknown_question_option'
    | 'dangling_next_item'
    | 'cycle'
    | 'unreachable_item'
    | 'unknown_cost_group'
    | 'cost_without_cost_group';

export interface GraphIssue {
    readonly code: GraphIssueCode;
    readonly message: string;
    readonly node_id?: TemplateNodeId;
    readonly item_id?: NodeItemId;
    readonly option_id?: number;
    readonly question_id?: QuestionId;
}

export interface TemplateGraph {
    readonly package: TemplatePackage;
    readonly roots: readonly GraphNode[];
    readonly nodesById: ReadonlyMap<TemplateNodeId, GraphNode>;
    readonly questionsById: ReadonlyMap<QuestionId, GraphQuestion>;
    readonly costGroupsById: ReadonlyMap<CostGroupId, PackageCostGroup>;
    /** Kapotte data uit de template; nooit een exception, altijd een melding. */
    readonly issues: readonly GraphIssue[];
}

/**
 * Bouwt de graaf uit een templatepakket. Puur: het pakket wordt niet gewijzigd.
 */
export function buildGraph(pkg: TemplatePackage): TemplateGraph {
    const issues: GraphIssue[] = [];
    const questionsById = indexQuestions(pkg.questions);
    const costGroupsById = new Map<CostGroupId, PackageCostGroup>(
        pkg.cost_groups.map((group) => [group.id, group]),
    );

    const nodesById = new Map<TemplateNodeId, GraphNode>();
    const childrenByParent = new Map<TemplateNodeId | null, PackageNode[]>();
    const knownIds = new Set<TemplateNodeId>();

    for (const node of pkg.nodes) {
        if (knownIds.has(node.id)) {
            issues.push({
                code: 'duplicate_node_id',
                message: `Knoop ${node.id} komt meer dan één keer voor.`,
                node_id: node.id,
            });
            continue;
        }

        knownIds.add(node.id);
    }

    const placed = new Set<TemplateNodeId>();

    for (const node of pkg.nodes) {
        if (placed.has(node.id)) {
            continue;
        }

        placed.add(node.id);
        let parentId = node.parent_id;

        if (parentId !== null && !knownIds.has(parentId)) {
            issues.push({
                code: 'missing_parent',
                message: `Knoop ${node.id} verwijst naar onbekende ouder ${parentId}; behandeld als wortel.`,
                node_id: node.id,
            });
            parentId = null;
        }

        const bucket = childrenByParent.get(parentId);

        if (bucket) {
            bucket.push(node);
        } else {
            childrenByParent.set(parentId, [node]);
        }
    }

    const roots = buildChildren(
        null,
        null,
        childrenByParent,
        nodesById,
        questionsById,
        costGroupsById,
        issues,
    );

    for (const node of pkg.nodes) {
        if (!nodesById.has(node.id)) {
            issues.push({
                code: 'missing_parent',
                message: `Knoop ${node.id} hangt niet onder een wortel en is niet bereikbaar.`,
                node_id: node.id,
            });
        }
    }

    return {
        package: pkg,
        roots,
        nodesById,
        questionsById,
        costGroupsById,
        issues,
    };
}

function indexQuestions(questions: readonly PackageQuestion[]): Map<QuestionId, GraphQuestion> {
    const index = new Map<QuestionId, GraphQuestion>();

    for (const question of questions) {
        const options = sortByOrder(question.options);

        index.set(question.id, {
            id: question.id,
            question,
            options,
            optionsById: new Map(options.map((option) => [option.id, option])),
            isMulti: MULTI_TYPES.has(question.type),
            isValueType: VALUE_TYPES.has(question.type),
        });
    }

    return index;
}

function buildChildren(
    parentId: TemplateNodeId | null,
    parent: GraphNode | null,
    childrenByParent: Map<TemplateNodeId | null, PackageNode[]>,
    nodesById: Map<TemplateNodeId, GraphNode>,
    questionsById: Map<QuestionId, GraphQuestion>,
    costGroupsById: Map<CostGroupId, PackageCostGroup>,
    issues: GraphIssue[],
): GraphNode[] {
    const children = sortByOrder(childrenByParent.get(parentId) ?? []);

    return children.map((node) => {
        const items = buildItems(node, questionsById, costGroupsById, issues);
        const itemsById = new Map(items.map((item) => [item.id, item]));
        const graphNode: GraphNode = {
            id: node.id,
            node,
            parent,
            children: [],
            items,
            itemsById,
            startItem: items.find((item) => item.isStart) ?? null,
            isMulti: node.is_multi,
        };

        nodesById.set(node.id, graphNode);
        validateItemGraph(graphNode, issues);

        (graphNode as { children: readonly GraphNode[] }).children = buildChildren(
            node.id,
            graphNode,
            childrenByParent,
            nodesById,
            questionsById,
            costGroupsById,
            issues,
        );

        return graphNode;
    });
}

function buildItems(
    node: PackageNode,
    questionsById: Map<QuestionId, GraphQuestion>,
    costGroupsById: Map<CostGroupId, PackageCostGroup>,
    issues: GraphIssue[],
): GraphItem[] {
    const seen = new Set<NodeItemId>();
    const items: GraphItem[] = [];

    for (const item of sortByOrder(node.items)) {
        if (seen.has(item.id)) {
            issues.push({
                code: 'duplicate_item_id',
                message: `Item ${item.id} komt meer dan één keer voor in knoop ${node.id}.`,
                node_id: node.id,
                item_id: item.id,
            });
            continue;
        }

        seen.add(item.id);
        const question = questionsById.get(item.question_id);

        if (!question) {
            issues.push({
                code: 'unknown_question',
                message: `Item ${item.id} in knoop ${node.id} verwijst naar onbekende vraag ${item.question_id}.`,
                node_id: node.id,
                item_id: item.id,
                question_id: item.question_id,
            });
        }

        const options = sortByOrder(item.options);

        for (const option of options) {
            if (question && !question.optionsById.has(option.question_option_id)) {
                issues.push({
                    code: 'unknown_question_option',
                    message: `Rand ${option.id} in knoop ${node.id} verwijst naar optie ${option.question_option_id} die niet meer bij vraag ${item.question_id} hoort.`,
                    node_id: node.id,
                    item_id: item.id,
                    option_id: option.id,
                    question_id: item.question_id,
                });
            }

            if (option.cost_group_id !== null && !costGroupsById.has(option.cost_group_id)) {
                issues.push({
                    code: 'unknown_cost_group',
                    message: `Rand ${option.id} in knoop ${node.id} verwijst naar onbekende kostengroep ${option.cost_group_id}.`,
                    node_id: node.id,
                    item_id: item.id,
                    option_id: option.id,
                });
            }
        }

        items.push({
            id: item.id,
            item,
            questionId: item.question_id,
            isStart: item.is_start,
            options,
        });
    }

    return items;
}

/** Meldt ontbrekende startitems, dangling randen, cycles en dode items. */
function validateItemGraph(node: GraphNode, issues: GraphIssue[]): void {
    if (node.items.length === 0) {
        return;
    }

    if (!node.startItem) {
        issues.push({
            code: 'missing_start_item',
            message: `Knoop ${node.id} heeft items maar geen startitem; het pad blijft leeg.`,
            node_id: node.id,
        });
    }

    for (const item of node.items) {
        for (const option of item.options) {
            if (option.next_item_id !== null && !node.itemsById.has(option.next_item_id)) {
                issues.push({
                    code: 'dangling_next_item',
                    message: `Rand ${option.id} in knoop ${node.id} verwijst naar item ${option.next_item_id} dat niet in deze knoop bestaat.`,
                    node_id: node.id,
                    item_id: item.id,
                    option_id: option.id,
                });
            }
        }
    }

    detectCycles(node, issues);
    detectUnreachableItems(node, issues);
}

function detectCycles(node: GraphNode, issues: GraphIssue[]): void {
    const state = new Map<NodeItemId, 'open' | 'done'>();

    const visit = (item: GraphItem): void => {
        state.set(item.id, 'open');

        for (const option of item.options) {
            const next =
                option.next_item_id === null ? undefined : node.itemsById.get(option.next_item_id);

            if (!next) {
                continue;
            }

            const seen = state.get(next.id);

            if (seen === 'open') {
                issues.push({
                    code: 'cycle',
                    message: `Knoop ${node.id} bevat een terugslag-kant van item ${item.id} naar item ${next.id}.`,
                    node_id: node.id,
                    item_id: item.id,
                    option_id: option.id,
                });
                continue;
            }

            if (seen === undefined) {
                visit(next);
            }
        }

        state.set(item.id, 'done');
    };

    for (const item of node.items) {
        if (!state.has(item.id)) {
            visit(item);
        }
    }
}

function detectUnreachableItems(node: GraphNode, issues: GraphIssue[]): void {
    if (!node.startItem) {
        return;
    }

    const reached = new Set<NodeItemId>();
    const stack: GraphItem[] = [node.startItem];

    while (stack.length > 0) {
        const item = stack.pop() as GraphItem;

        if (reached.has(item.id)) {
            continue;
        }

        reached.add(item.id);

        for (const option of item.options) {
            const next =
                option.next_item_id === null ? undefined : node.itemsById.get(option.next_item_id);

            if (next && !reached.has(next.id)) {
                stack.push(next);
            }
        }
    }

    for (const item of node.items) {
        if (!reached.has(item.id)) {
            issues.push({
                code: 'unreachable_item',
                message: `Item ${item.id} in knoop ${node.id} is vanaf het startitem niet te bereiken.`,
                node_id: node.id,
                item_id: item.id,
            });
        }
    }
}

/** Stabiele sortering op `sort_order`, met `id` als tiebreak. */
function sortByOrder<T extends { id: number; sort_order: number }>(values: readonly T[]): T[] {
    return [...values].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
}
